import type { AttackKind, Combatant } from './simulation'
import type { HeroOutcome } from './heroMotion'
import { isRigAnimationClipQuarantined } from './rigAnimationQuarantine'

export type AnimationSemantic =
  | 'idle' | 'walk' | 'backward' | 'backstep' | 'run' | 'dash' | 'depth' | 'jump' | 'air' | 'flight' | 'dive' | 'land' | 'block' | 'parry'
  | 'hit' | 'downed' | 'getup' | 'victory' | 'defeat'
  | 'light' | 'heavy' | 'special' | 'throw' | 'generic-attack' | 'unknown'

export type AnimationMotionContext = {
  vx: number
  vz: number
  speedRatio: number
  planarSpeed: number
  outcome: HeroOutcome
  retreating?: boolean
  
  backward?: boolean
advancing?: boolean
  lateral01?: number
  gait?: 'walk'|'run'
  dashing?: boolean
}

export type AnimationClipDescriptor = { name:string; semantic:AnimationSemantic; score:number }
export type AnimationVariationPlan = {
  clip:string
  semantic:AnimationSemantic
  playbackRate:number
  crossFadeSeconds:number
  loop:boolean
  candidateCount:number
  seed:number
  reverse?:boolean
}

const families:Array<[AnimationSemantic,RegExp,number]> = [
  ['throw',/grab|throw|grapple|takedown|clinch|slam/i,100],
  ['heavy',/heavy|smash|power.?attack|attack.?2|slash.?2|swing.?2|uppercut/i,105],
  ['light',/light|jab|punch|kick|attack.?1|slash.?1|swing.?1|strike/i,104],
  ['special',/special|skill|ultimate|spell|cast|magic|ability|attack.?3|power/i,100],
  ['getup',/get.?up|stand.?up|recover|rise/i,89],
  ['downed',/down|knockdown|fall.?down|collapse|ko/i,88],
  ['defeat',/defeat|death|lose|loss/i,87],
  ['victory',/victory|winner|celebrat|taunt|(?:^|[^a-z])win(?:$|[^a-z])/i,86],
  ['hit',/hit|hurt|damage|stagger|impact|react/i,84],
  ['parry',/parry|perfect.?guard|deflect/i,83],
  ['block',/block|guard|defend/i,82],
  ['dive',/dive|ground.?pound|slam.?down|plunge|power.?drop/i,85],
  ['flight',/fly|flight|hover|levitat|glide|soar/i,84],
  ['land',/land|landing|touch.?down|impact.?land/i,83],
  ['jump',/jump|leap|take.?off|hop/i,82],
  ['air',/fall|air.?idle|airborne|float|descent|apex/i,81],
  ['dash',/dash|dodge|roll|evade|sidestep|sprint/i,80],
  ['depth',/strafe|side.?step|sidewalk|lateral/i,76],
  ['backstep',/back.?step|reverse.?step|retreat.?step/i,78],
  ['backward',/back.?pedal|walk.?back|move.?back|retreat|reverse.?walk/i,75],
  ['run',/run|jog|locomotion/i,72],
  ['walk',/walk|move/i,70],
  ['idle',/idle|stand|breath|ready|stance|baselayer/i,60],
  ['generic-attack',/attack|swing|slash|melee|combat/i,50],
]

export function classifyAnimationClip(name:string):AnimationClipDescriptor {
  for(const [semantic,pattern,score] of families) if(pattern.test(name)) return {name,semantic,score}
  return {name,semantic:'unknown',score:0}
}
export function isQuarantinedAnimationClip(name:string,fighterId?:string){return isRigAnimationClipQuarantined(fighterId,name)}
export function catalogueAnimationClips(names:readonly string[],fighterId?:string){return names.filter(name=>!isQuarantinedAnimationClip(name,fighterId)).map(classifyAnimationClip)}
function hashSeed(text:string){let hash=2166136261;for(let i=0;i<text.length;i++){hash^=text.charCodeAt(i);hash=Math.imul(hash,16777619)}return hash>>>0}

export function animationSemanticForState(state:Combatant,motion:AnimationMotionContext):AnimationSemantic {
  if(state.downed) return 'downed'
  if(motion.outcome==='defeat') return 'defeat'
  if(state.hitstun>0) return 'hit'
  if(state.wakeInvuln>0) return 'getup'
  if(state.blocking) return state.parryWindow>0?'parry':'block'
  if(state.attackPhase&&state.lastAttack) return state.lastAttack
  if(motion.outcome==='victory') return 'victory'
  if(state.aerialEvent==='land'&&state.aerialEventTime>0) return 'land'
  if(state.aerialMode==='flight') return 'flight'
  if(state.aerialMode==='dive') return 'dive'
  if(state.aerialMode==='ascent') return 'jump'
  if(state.aerialMode==='apex'||state.aerialMode==='descent') return 'air'
  const planar=Math.hypot(motion.vx,motion.vz)
  if(planar<.08) return 'idle'
  if(Math.abs(motion.vz)>Math.abs(motion.vx)*.72) return 'depth'
  if(motion.backward) return 'backstep'
  if(motion.retreating) return 'backward'
  if(state.dashTimer>0) return 'dash'
  if(motion.gait==='run') return 'run'
  if(motion.gait==='walk') return 'walk'
  if(motion.speedRatio>=.62) return 'run'
  return 'walk'
}

const fallbackOrder:Record<AnimationSemantic,AnimationSemantic[]> = {
  idle:['idle','walk'],walk:['walk','run','idle'],backward:['backward','walk','run','idle'],backstep:['backstep','backward','walk','run','idle'],run:['run','walk','dash','idle'],dash:['dash','run','walk','idle'],depth:['depth','run','walk','idle'],
  jump:['jump','air','run','idle'],air:['air','jump','flight','idle'],flight:['flight','air','idle'],dive:['dive','air','special','run'],land:['land','idle','walk'],
  block:['block','idle'],parry:['parry','block','hit','idle'],hit:['hit','block','idle'],downed:['downed','defeat','idle'],getup:['getup','idle'],
  victory:['victory','idle'],defeat:['defeat','downed','idle'],light:['light','generic-attack','heavy'],heavy:['heavy','generic-attack','light'],
  special:['special','generic-attack','heavy'],throw:['throw','generic-attack','heavy'], 'generic-attack':['generic-attack','light','heavy'],unknown:['idle']
}
function candidatesFor(catalogue:AnimationClipDescriptor[],semantic:AnimationSemantic){
  for(const candidate of fallbackOrder[semantic]){
    const exact=catalogue.filter(c=>c.semantic===candidate)
    if(exact.length) return exact
  }
  return catalogue.filter(c=>c.semantic!=='unknown').length?catalogue.filter(c=>c.semantic!=='unknown'):catalogue
}

export function planFighterAnimation(
  names:readonly string[],state:Combatant,fighterId:string,motion:AnimationMotionContext,actionOrdinal=0,
):AnimationVariationPlan|null {
  if(!names.length)return null
  const catalogue=catalogueAnimationClips(names,fighterId),semantic=animationSemanticForState(state,motion),candidates=candidatesFor(catalogue,semantic)
  if(!candidates.length)return null
  const attack:AttackKind|'neutral'=state.attackPhase&&state.lastAttack?state.lastAttack:'neutral'
  const seed=hashSeed(`${fighterId}|${semantic}|${attack}|${state.comboCount}|${actionOrdinal}`)
  const chosen=candidates[seed%candidates.length],rateBand=((seed>>>8)%9)-4
  const base=semantic==='dash'?1.08:semantic==='run'?1.04:semantic==='special'?1.08:semantic==='heavy'||semantic==='throw'?.99:1
  const locomotionScale=['walk','backward','backstep','run','dash','depth'].includes(semantic)?Math.max(.74,Math.min(1.2,.8+motion.speedRatio*.24)):['jump','air','flight','dive','land'].includes(semantic)?Math.max(.82,Math.min(1.1,.9+motion.speedRatio*.12)):1
  const playbackRate=Math.max(.76,Math.min(1.18,(base+rateBand*.008)*locomotionScale))
  const crossFadeSeconds=semantic==='hit'||semantic==='downed'?.05:semantic==='getup'?.07:semantic==='idle'?.16:['walk','backward','backstep','run','depth'].includes(semantic)?.115:semantic==='land'?.08:['jump','air','flight','dive'].includes(semantic)?.095:.075
  const loop=['idle','walk','backward','backstep','run','dash','depth','air','flight','block','parry'].includes(semantic)
  const reverse=(semantic==='backward'||semantic==='backstep')&&chosen.semantic!=='backward'&&chosen.semantic!=='backstep'
  return{clip:chosen.name,semantic,playbackRate,crossFadeSeconds,loop,candidateCount:candidates.length,seed,reverse}
}

export function animationCoverage(names:readonly string[]){
  const catalogue=catalogueAnimationClips(names)
  return{total:catalogue.length,classified:catalogue.filter(c=>c.semantic!=='unknown').length,unknown:catalogue.filter(c=>c.semantic==='unknown').map(c=>c.name),semantics:[...new Set(catalogue.map(c=>c.semantic))]}
}
